import { useState, useEffect, useMemo } from "react";
import { TextField, Typography, Alert, Box, Tooltip } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { isAxiosError } from "axios";
import { extractErrorDetail } from "src/helpers/apiError";
import { ModalBase } from "./ModelBase";
import {
  useDeleteDataset,
  useDeleteDatasetFolder,
} from "src/views/datasets/services/DatasetServices";
import { useQueryClient } from "react-query";
import { NucToast } from "src/reusable/components/Toast";
import "../formUtils.scss";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

type Props = {
  isVisible: boolean;
  onClose: () => void;
  id: number;
  datasetName: string;
  isFolder?: boolean;
};

export function DeleteDatasetModal({
  isVisible,
  onClose,
  id,
  datasetName,
  isFolder = false,
}: Props) {
  const queryClient = useQueryClient();

  // State
  const [confirmationText, setConfirmationText] = useState<string>("");

  // Mutations
  const deleteDatasetMutation = useDeleteDataset();
  const deleteDatasetFolderMutation = useDeleteDatasetFolder();

  // Determine which mutation to use
  const activeMutation = isFolder
    ? deleteDatasetFolderMutation
    : deleteDatasetMutation;

  // Handlers
  const handleClose = () => {
    setConfirmationText("");
    onClose();
  };

  // Computed values
  const itemName = datasetName || "";
  const itemType = isFolder ? "dataset folder" : "dataset";
  const itemTypeCapitalized = isFolder ? "Dataset Folder" : "Dataset";

  const handleSubmit = () => {
    if (!id) return;

    if (isFolder) {
      // @ts-expect-error: mutate is expecting void rather than an id
      deleteDatasetFolderMutation.mutate(id);
    } else {
      deleteDatasetMutation.mutate({ id });
    }
  };

  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(itemName);
    NucToast({
      message: "Copied to clipboard!",
      type: "info",
    });
  };

  // Effects
  useEffect(() => {
    if (activeMutation.isSuccess) {
      if (isFolder) {
        queryClient.invalidateQueries("datasetFolders");
      }
      queryClient.invalidateQueries("datasetList");
      handleClose();
    }
  }, [activeMutation.isSuccess, itemTypeCapitalized, isFolder, queryClient]);

  const isConfirmationValid = useMemo(() => {
    return confirmationText.trim().toLowerCase() === itemName.toLowerCase();
  }, [confirmationText, itemName]);

  const hasError = useMemo(() => {
    return activeMutation.isError && !isAxiosError(activeMutation.error);
  }, [activeMutation.isError, activeMutation.error]);

  const errorMessage = useMemo(() => {
    if (isAxiosError(activeMutation.error)) {
      return extractErrorDetail(
        activeMutation.error,
        `Failed to delete ${itemType}`,
      );
    }
    return hasError ? `Something went wrong while deleting ${itemType}` : null;
  }, [activeMutation.error, hasError, itemType]);

  if (!id || !datasetName) {
    return null;
  }

  return (
    <ModalBase
      title={`Delete ${itemTypeCapitalized}`}
      isVisible={isVisible}
      onClose={handleClose}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Error Alert */}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {/* Warning Alert */}
        <Alert severity="warning">
          <Typography variant="body2">
            Are you sure you want to delete
            <Tooltip title="Copy Text">
              <strong
                onClick={handleCopyToClipboard}
                className="clickable-copy-text"
                data-testid="ds-modal-delete-name"
              >
                <ContentCopyIcon sx={{ width: "16px", height: "16px" }} />
                {itemName}
              </strong>
            </Tooltip>
            ? This action is <strong>non-reversible</strong>. You are about to
            permanently delete it?
            {isFolder && (
              <> This will also delete any datasets inside this folder.</>
            )}
          </Typography>
        </Alert>

        {/* Confirmation Input */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            To confirm deletion, type the {itemType} name exactly as shown
            above:
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={`Type "${itemName}" to confirm`}
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            data-testid="ds-modal-delete-input-confirm"
            error={confirmationText.length > 0 && confirmationText !== itemName}
            helperText={
              confirmationText.length > 0 && confirmationText !== itemName
                ? "Name does not match"
                : ""
            }
          />
        </Box>

        {/* Delete Button */}
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={!isConfirmationValid}
          loading={activeMutation.isLoading}
          data-testid="ds-modal-delete-btn-submit"
          sx={{ width: "fit-content", ml: "auto" }}
        >
          Delete {itemTypeCapitalized}
        </LoadingButton>
      </Box>
    </ModalBase>
  );
}
